import type { Website } from 'types';
import type { TrackingMethodChoice } from './tracking-methods';
import { useState } from 'react';
import { can } from 'definitions/capabilities';
import { canUseServerSide } from 'definitions/server-side-access';
import { useAuth } from 'hooks/useAuth';
import { useServerKeys } from '@/state/useServerKeys';
import { trackingMethodOptions } from './tracking-methods';

type TrackingMethodChoiceParams = {
  website: Website;
  /** Whether the settings form is on screen; the key list is only fetched while it is. */
  enabled: boolean;
  setTrackingMethod: (next: TrackingMethodChoice) => void;
};

type NeedsConfirmParams = { website: Pick<Website, 'trackingMethod'>; next: TrackingMethodChoice; activeKeys: number };

/**
 * Switching to 'server' with no active key stops every conversion on the website until a
 * backend reports them, so it asks first. Still allowed: a customer may set the method
 * ahead of the key. A website already on 'server' is not asked again.
 */
export const needsServerConfirm = ({ website, next, activeKeys }: NeedsConfirmParams) =>
  next === 'server' && website.trackingMethod !== 'server' && activeKeys === 0;

/**
 * The tracking-method radio's choice, with the Server-side confirm in front of it. Someone
 * who cannot read the keys (not `server-keys:manage`, or not yet allowed server-side, PER-178)
 * counts as having none, so is asked; the key list is never requested for them, since the API
 * would refuse it.
 */
export const useTrackingMethodChoice = ({ website, enabled, setTrackingMethod }: TrackingMethodChoiceParams) => {
  const { user } = useAuth();
  const allowServer = canUseServerSide(user);
  const canReadKeys = can(user, 'server-keys:manage') && allowServer;
  const options = trackingMethodOptions({ allowServer, current: website.trackingMethod });
  const { activeKeys } = useServerKeys(website.id, enabled && canReadKeys);
  const [confirmingServer, setConfirmingServer] = useState(false);

  const choose = (next: TrackingMethodChoice) => {
    if (needsServerConfirm({ website, next, activeKeys: activeKeys.length })) {
      setConfirmingServer(true);
      return;
    }
    setTrackingMethod(next);
  };

  const confirmServer = () => {
    setTrackingMethod('server');
    setConfirmingServer(false);
  };

  const cancelServer = () => setConfirmingServer(false);

  return { options, choose, confirmingServer, confirmServer, cancelServer };
};

export type TrackingMethodChoiceManager = ReturnType<typeof useTrackingMethodChoice>;
